import React from "react";
import { FiPlus } from "react-icons/fi";
import s from "./PustyStan.module.css";

/**
 * PUSTY STAN (W-1, audyt UI 2026-09-25 poz. 5 / U8) — JEDNA biała karta dla całego systemu.
 *
 * Właściciel: „system nie wymaga nauczania, tylko user ma podane na tacy to, co ma robić".
 * Stąd każdy pusty stan ma TRZY obowiązkowe rzeczy: ikonę, tytuł („co tu nie ma") i opis
 * („co zrobić") — a tam, gdzie użytkownik może coś zrobić od razu, PRZYCISK, który woła
 * DOKŁADNIE tę samą akcję, co akcja główna paska (`.primaryBtn`, etykieta „Dodaj + biernik",
 * DS §13.15). Pusty ekran bez podpowiedzi to ekran, którego trzeba się nauczyć.
 *
 * Karta ma ramkę i tło z tokenów (`--color-border`, `--color-bg-primary`), bo 69 arkuszy
 * rysowało pusty stan „w powietrzu" — w jasnym motywie `bg-primary` = `bg-secondary` = #fff,
 * więc bez ramki stan pusty był niewidoczny (pułapka DS §12). Klasa kontenera składa się z
 * globalnej `.emptyState` (`styles/global.css`), która jest siatką bezpieczeństwa także dla
 * arkuszy jeszcze nieprzepiętych.
 *
 * Błąd (`blad`) NIE wygląda jak pusta baza: własny ton ikony, `role="alert"`, `data-stan="blad"`.
 *
 * @param {Node}    ikona    react-icon (bez rozmiaru — kafel nadaje 1.375rem).
 * @param {string}  tytul    WYMAGANY. Krótko: „Brak zgłoszeń", „Brak wyników wyszukiwania".
 * @param {Node}    opis     WYMAGANY. Jedno zdanie: co zrobić / dlaczego pusto.
 * @param {object|Node} akcja `{ etykieta, onClick, ikona?, disabled?, testId? }` → przycisk
 *                           `.primaryBtn`; albo gotowy węzeł (np. własny przycisk).
 * @param {boolean} blad     stan błędu zamiast pustej listy.
 * @param {boolean} kompakt  mniejszy padding (wiersz tabeli, panel boczny).
 * @param {Node}    children dodatkowe akcje drugorzędne (`.ghostBtn`: „Wyczyść filtry", „Ponów").
 */
const PustyStan = ({
    ikona,
    tytul,
    opis,
    akcja,
    blad = false,
    kompakt = false,
    children,
    className,
    testId,
    ...reszta
}) => {
    const klasy = [s.karta, blad ? s.blad : "", kompakt ? s.kompakt : "", className].filter(Boolean).join(" ");
    const przycisk = renderujAkcje(akcja);
    return (
        <div
            className={klasy}
            data-stan={blad ? "blad" : "pusto"}
            data-testid={testId}
            role={blad ? "alert" : undefined}
            {...reszta}
        >
            {ikona ? (
                <span className={s.ikona} aria-hidden="true">
                    {ikona}
                </span>
            ) : null}
            <h3 className={s.tytul}>{tytul}</h3>
            {opis ? <p className={s.opis}>{opis}</p> : null}
            {przycisk || children ? (
                <div className={s.akcje}>
                    {przycisk}
                    {children}
                </div>
            ) : null}
        </div>
    );
};

const renderujAkcje = (akcja) => {
    if (!akcja) return null;
    if (React.isValidElement(akcja)) return akcja;
    const { etykieta, onClick, ikona, disabled, testId } = akcja;
    if (!etykieta || typeof onClick !== "function") return null;
    return (
        <button type="button" className="primaryBtn" onClick={onClick} disabled={!!disabled} data-testid={testId}>
            {ikona === null ? null : ikona || <FiPlus size={14} />}
            {etykieta}
        </button>
    );
};

export default PustyStan;
